import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import BlogCoverArt from '../components/BlogCoverArt';
import { getPostBySlug, getPublishedPostsOnce } from '../data/blogService';

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const SHARE_ICONS = {
  linkedin: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  ),
  x: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.736-8.849L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  link: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.07 0l2.83-2.83a5 5 0 00-7.07-7.07l-1.5 1.5" />
      <path d="M14 11a5 5 0 00-7.07 0L4.1 13.83a5 5 0 007.07 7.07l1.49-1.49" />
    </svg>
  ),
};

function Block({ block, accent }) {
  if (block.type === 'h2') {
    return (
      <h2
        style={{
          fontFamily: "'Anton', sans-serif",
          fontWeight: 400,
          fontSize: 'clamp(20px, 2.6vw, 28px)',
          lineHeight: 1.2,
          color: 'var(--ink)',
          margin: '34px 0 14px',
        }}
      >
        {block.text}
      </h2>
    );
  }
  if (block.type === 'p') {
    return <p style={{ fontSize: 16, lineHeight: 1.85, color: 'var(--ink-soft)', marginBottom: 18 }}>{block.text}</p>;
  }
  if (block.type === 'ul') {
    return (
      <ul style={{ margin: '4px 0 22px', paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {block.items.map((item, i) => (
          <li key={i} style={{ display: 'flex', gap: 12, fontSize: 15.5, lineHeight: 1.65, color: 'var(--ink-soft)' }}>
            <span style={{ flexShrink: 0, width: 6, height: 6, borderRadius: '50%', background: accent, marginTop: 9 }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote
        style={{
          margin: '28px 0',
          padding: '22px 26px',
          borderLeft: `3px solid ${accent}`,
          background: `rgba(${accent === 'var(--ink)' ? '10,10,20' : '0,0,0'},0.03)`,
          borderRadius: '0 14px 14px 0',
        }}
      >
        <p style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontSize: 'clamp(18px, 2.1vw, 22px)', lineHeight: 1.5, color: 'var(--ink)', marginBottom: block.cite ? 10 : 0 }}>
          “{block.text}”
        </p>
        {block.cite && (
          <span style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: accent }}>
            {block.cite}
          </span>
        )}
      </blockquote>
    );
  }
  return null;
}

function RecentBlogItem({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="hoverable"
      style={{ display: 'flex', gap: 12, alignItems: 'center' }}
    >
      <div style={{ width: 56, height: 56, borderRadius: 12, overflow: 'hidden', flexShrink: 0 }}>
        <BlogCoverArt image={post.image} alt={post.title} rgb={post.rgb} compact height="100%" />
      </div>
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.35, marginBottom: 4,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}
        >
          {post.title}
        </p>
        <span style={{ fontSize: 11.5, color: post.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          {post.categoryLabel}
        </span>
      </div>
    </Link>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    Promise.all([getPostBySlug(slug), getPublishedPostsOnce()])
      .then(([foundPost, posts]) => {
        if (cancelled) return;
        setPost(foundPost);
        setAllPosts(posts);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load blog post:', err);
        setLoadError(true);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <section style={{ padding: '160px 32px', textAlign: 'center', color: 'var(--ink-soft)' }}>
        Loading article…
      </section>
    );
  }

  if (loadError) {
    return (
      <section style={{ padding: '160px 32px', textAlign: 'center' }}>
        <p style={{ color: 'var(--ink-soft)', marginBottom: 16 }}>
          Something went wrong loading this article. Please try again.
        </p>
        <Link to="/blog" style={{ color: 'var(--red)', fontWeight: 700 }}>← Back to Blog</Link>
      </section>
    );
  }

  if (!post) return <Navigate to="/blog" replace />;

  const recent = allPosts.filter((p) => p.slug !== post.slug).slice(0, 4);
  const url = `https://zone-x.dev/blog/${post.slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable — silently ignore */
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { '@type': 'Person', name: post.author },
    publisher: { '@type': 'Organization', name: 'Zone-X Laboratories' },
    mainEntityOfPage: url,
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Zone-X Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section style={{ padding: '128px 0 0' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 32px' }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
            <Link to="/" style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>Home</Link>
            <Crumb />
            <Link to="/blog" style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>Blog</Link>
            <Crumb />
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: post.accent }}>{post.categoryLabel}</span>
          </div>

          {/* Title block */}
          <div style={{ maxWidth: 880, marginBottom: 28 }}>
            <div
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, width: 'fit-content',
                padding: '6px 14px', borderRadius: 999, marginBottom: 18,
                border: `1.4px solid ${post.accent}`, color: post.accent,
                fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: post.accent }} />
              {post.categoryLabel}
            </div>
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontWeight: 400,
                fontSize: 'clamp(32px, 5.2vw, 58px)',
                lineHeight: 1.06,
                color: 'var(--ink)',
                marginBottom: 22,
              }}
            >
              {post.title}
            </h1>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div
                style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: `rgba(${post.rgb},0.12)`, color: post.accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 14, flexShrink: 0,
                }}
              >
                {post.author.split(' ').map((w) => w[0]).join('').slice(0, 2)}
              </div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>{post.author}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{post.role}</div>
              </div>
              <span style={{ opacity: 0.35 }}>•</span>
              <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>{formatDate(post.date)}</span>
              <span style={{ opacity: 0.35 }}>•</span>
              <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>{post.readTime}</span>
            </div>
          </div>

          {/* Content grid: article (image + body) + sidebar, beside each other from the top */}
          <div className="blog-post-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2.15fr) minmax(0,1fr)', gap: 56, paddingBottom: 80 }}>
            {/* Article body */}
            <article>
              {/* Hero cover — sits beside the sidebar, not above it */}
              <div style={{ borderRadius: 22, overflow: 'hidden', aspectRatio: '16 / 9', marginBottom: 36 }}>
                <BlogCoverArt image={post.image} alt={post.title} rgb={post.rgb} height="100%" />
              </div>

              {post.body.map((block, i) => (
                <Block key={i} block={block} accent={post.accent} />
              ))}

              {/* Social share */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 40, paddingTop: 28, borderTop: '1px solid var(--line)' }}>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>Share</span>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
                  target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn"
                  className="hoverable"
                  style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink)' }}
                >
                  {SHARE_ICONS.linkedin}
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank" rel="noopener noreferrer" aria-label="Share on X"
                  className="hoverable"
                  style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink)' }}
                >
                  {SHARE_ICONS.x}
                </a>
                <button
                  onClick={handleCopy}
                  aria-label="Copy link"
                  className="hoverable"
                  style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink)', position: 'relative' }}
                >
                  {SHARE_ICONS.link}
                  {copied && (
                    <span style={{ position: 'absolute', top: -30, left: '50%', transform: 'translateX(-50%)', fontSize: 11, background: 'var(--ink)', color: 'white', padding: '4px 9px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                      Copied!
                    </span>
                  )}
                </button>
              </div>
            </article>

            {/* Sidebar — Recent Blogs + Newsletter only (no categories, no comments) */}
            <aside className="blog-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
              <div>
                <h3 style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--red)', marginBottom: 18 }}>
                  Recent Blogs
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {recent.map((p) => (
                    <RecentBlogItem key={p.slug} post={p} />
                  ))}
                </div>
              </div>

              <div
                style={{
                  padding: 24, borderRadius: 18,
                  background: 'linear-gradient(145deg, rgba(0,60,255,0.06), rgba(255,0,4,0.05))',
                  border: '1px solid var(--line)',
                }}
              >
                <h3 style={{ fontFamily: "'Anton', sans-serif", fontWeight: 400, fontSize: 17, color: 'var(--ink)', marginBottom: 8 }}>
                  Join the Zone-X Newsletter
                </h3>
                <p style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.6, marginBottom: 16 }}>
                  Engineering notes on IoT, embedded systems, and edge AI — straight to your inbox, roughly once a month.
                </p>
                <form
                  onSubmit={(e) => e.preventDefault()}
                  style={{ display: 'flex', gap: 8 }}
                >
                  <input
                    type="email"
                    required
                    placeholder="you@company.com"
                    aria-label="Email address"
                    style={{
                      flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 10,
                      border: '1px solid var(--line)', fontSize: 12.5, fontFamily: 'inherit', outline: 'none',
                    }}
                  />
                  <button
                    type="submit"
                    className="hoverable"
                    style={{ padding: '10px 16px', borderRadius: 10, background: 'var(--ink)', color: 'white', fontSize: 12.5, fontWeight: 700, flexShrink: 0 }}
                  >
                    Sign up
                  </button>
                </form>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

function Crumb() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ opacity: 0.3, flexShrink: 0 }}>
      <path d="M5 3l4 4-4 4" stroke="var(--ink)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
